import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { subsidiaries } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import useDocumentTitle from '../hooks/useDocumentTitle';
import SubsidiaryIcon from '../components/ui/SubsidiaryIcon';
import POSTSection from '../components/sections/POSTSection';
import MENTISSection from '../components/sections/MENTISSection';
import OceanaSection from '../components/sections/OceanaSection';
import VeenaSection from '../components/sections/VeenaSection';
import SIRIUSSection from '../components/sections/SIRIUSSection';
import AEDISSection from '../components/sections/AEDISSection';
import OcsasSection from '../components/sections/OcsasSection';

const SubsidiaryPage = () => {
  const { id } = useParams();
  const { language, t, pick } = useLanguage();
  const subsidiary = subsidiaries.find(s => s.id === id);
  useDocumentTitle(subsidiary ? subsidiary.name : '404');

  if (!subsidiary) {
    return <Navigate to="/ecosystem" replace />;
  }

  const sub = subsidiary.i18n[language];
  const currentIndex = subsidiaries.findIndex(s => s.id === id);
  const prevSub = currentIndex > 0 ? subsidiaries[currentIndex - 1] : null;
  const nextSub = currentIndex < subsidiaries.length - 1 ? subsidiaries[currentIndex + 1] : null;
  const isSmartland = subsidiary.id === 'smartland';
  const isSmartmedias = subsidiary.id === 'smartmedias';
  const isSmartlab = subsidiary.id === 'smartlab';
  const isSmartmart = subsidiary.id === 'smartmart';
  const isSmartinvest = subsidiary.id === 'smartinvest';

  const e = {
    en: { whatWeDo: 'What We Do', ourFocus: 'Our Focus', principle: 'Our Principle', whyChoose: 'Why Choose', ourJourney: 'Our Journey', milestones: 'Milestones & Projects', founders: 'Founders', viewAll: 'View All Companies', previous: 'Previous', next: 'Next', interested: 'Interested in', letsDiscuss: "Let's discuss partnership opportunities." },
    fr: { whatWeDo: 'Ce que nous faisons', ourFocus: 'Notre focus', principle: 'Notre principe', whyChoose: 'Pourquoi choisir', ourJourney: 'Notre parcours', milestones: 'Étapes & Projets', founders: 'Fondateurs', viewAll: 'Voir toutes les entreprises', previous: 'Précédent', next: 'Suivant', interested: 'Intéressé par', letsDiscuss: 'Discutons des opportunités de partenariat.' }
  }[language];

  return (
    <div className="pt-20">
      <section className="section-padding-lg bg-[#1a1c1b] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-3xl" style={{ backgroundColor: subsidiary.color }}></div>
        </div>

        <div className="container-swg relative z-10">
          <div className="flex items-center gap-2 mb-8">
            <Link to="/ecosystem" className="text-[#9AACB8] hover:text-[#5B7A8A] transition-colors text-sm">{t('nav.ecosystem')}</Link>
            <span className="text-[#9AACB8]">/</span>
            <span className="text-[#5B7A8A] text-sm">{subsidiary.name}</span>
          </div>

          <div className="flex items-start gap-6 mb-8">
            <SubsidiaryIcon subsidiary={subsidiary} size="xl" />
            <div>
              <h1 className="heading-1 text-white mb-2">{subsidiary.name}</h1>
              <p className="text-xl font-medium" style={{ color: subsidiary.color }}>{sub.tagline}</p>
            </div>
          </div>

          <div className="accent-line mb-8" style={{ backgroundColor: subsidiary.color }}></div>
          <p className="body-large max-w-3xl">{sub.fullDescription || sub.about}</p>
        </div>
      </section>

      <section className="section-padding bg-[#302f2c]/30 border-t border-[rgba(91,122,138,0.3)]">
        <div className="container-swg">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <span className="caption block mb-4" style={{ color: subsidiary.color }}>{e.whatWeDo}</span>
              <h2 className="heading-2 text-white mb-6">{t('subsidiary.coreActivities')}</h2>
              <div className="accent-line mb-8" style={{ backgroundColor: subsidiary.color }}></div>

              <ul className="space-y-4">
                {sub.coreActivities.map((activity, index) => (
                  <li key={index} className="flex items-start gap-4 p-4 swg-card rounded-xl">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${subsidiary.color}20` }}>
                      <Check className="w-4 h-4" style={{ color: subsidiary.color }} />
                    </div>
                    <span className="text-white font-medium">{activity}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <span className="caption block mb-4" style={{ color: subsidiary.color }}>{e.ourFocus}</span>
              <h2 className="heading-2 text-white mb-6">{t('subsidiary.strategicFocus')}</h2>
              <div className="accent-line mb-8" style={{ backgroundColor: subsidiary.color }}></div>

              <ul className="space-y-4">
                {sub.strategicFocus.map((focus, index) => (
                  <li key={index} className="flex items-start gap-4 p-4 swg-card rounded-xl">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${subsidiary.color}20` }}>
                      <span className="font-bold text-sm" style={{ color: subsidiary.color }}>{index + 1}</span>
                    </div>
                    <span className="text-white font-medium">{focus}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {isSmartland && sub.principle && (
        <section className="py-20 bg-[#E07B54]/10">
          <div className="container-swg">
            <div className="max-w-4xl mx-auto text-center">
              <span className="caption block mb-6" style={{ color: subsidiary.color }}>{e.principle}</span>
              <p className="text-xl md:text-2xl text-white leading-relaxed">"{sub.principle}"</p>
            </div>
          </div>
        </section>
      )}

      <section className="section-padding bg-[#1a1c1b]">
        <div className="container-swg">
          <div className="max-w-4xl mx-auto text-center">
            <span className="caption block mb-4" style={{ color: subsidiary.color }}>{t('subsidiary.valueProposition')}</span>
            <h2 className="heading-2 text-white mb-8">{e.whyChoose} {subsidiary.name}</h2>
            <div className="accent-line mx-auto mb-8" style={{ backgroundColor: subsidiary.color }}></div>

            <div className="swg-card rounded-2xl p-10" style={{ borderColor: `${subsidiary.color}30` }}>
              <p className="body-large text-white leading-relaxed">{sub.valueProposition}</p>
            </div>
          </div>
        </div>
      </section>

      {isSmartmedias && <POSTSection accentColor={subsidiary.color} />}
      {isSmartlab && (
        <>
          <MENTISSection accentColor={subsidiary.color} />
          <VeenaSection />
          <OcsasSection />
        </>
      )}
      {isSmartmart && <OceanaSection />}
      {isSmartinvest && <SIRIUSSection />}
      {isSmartland && <AEDISSection />}

      <section className="section-padding bg-[#302f2c]/30 border-t border-[rgba(91,122,138,0.3)]">
        <div className="container-swg">
          <div className="flex flex-col md:flex-row justify-between items-stretch gap-6">
            {prevSub ? (
              <Link to={`/subsidiary/${prevSub.id}`} className="swg-card rounded-xl p-6 flex-1 group hover:border-[#5B7A8A]/50 transition-all">
                <div className="flex items-center gap-4">
                  <ArrowLeft className="w-5 h-5 text-[#9AACB8] group-hover:text-[#5B7A8A] transition-colors" />
                  <div>
                    <span className="text-[#9AACB8] text-sm">{e.previous}</span>
                    <p className="text-white font-semibold">{prevSub.name}</p>
                  </div>
                </div>
              </Link>
            ) : (
              <div className="flex-1"></div>
            )}

            <Link to="/ecosystem" className="btn-secondary">{e.viewAll}</Link>

            {nextSub ? (
              <Link to={`/subsidiary/${nextSub.id}`} className="swg-card rounded-xl p-6 flex-1 group hover:border-[#5B7A8A]/50 transition-all text-right">
                <div className="flex items-center justify-end gap-4">
                  <div>
                    <span className="text-[#9AACB8] text-sm">{e.next}</span>
                    <p className="text-white font-semibold">{nextSub.name}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#9AACB8] group-hover:text-[#5B7A8A] transition-colors" />
                </div>
              </Link>
            ) : (
              <div className="flex-1"></div>
            )}
          </div>
        </div>
      </section>

      <section className="section-padding bg-[#1a1c1b] border-t border-[rgba(91,122,138,0.3)]">
        <div className="container-swg">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="heading-3 text-white mb-2">{e.interested} {subsidiary.name}?</h2>
              <p className="text-[#9AACB8]">{e.letsDiscuss}</p>
            </div>
            <div className="flex gap-4">
              <Link to="/partnerships" className="btn-primary">{t('cta.partnerWithUs')}</Link>
              <Link to="/contact" className="btn-secondary">{t('cta.contactUs')}</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SubsidiaryPage;
